import { type FormEvent, useState } from "react";
import { useNavigate, Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Cta } from "../components/Cta";
import { Section } from "../components/Section";
import { ConsentNotice } from "../features/legal/ConsentNotice";
import { useAuth } from "../features/auth/context";
import { useMe, meKey } from "../features/catalogue/queries";
import { api } from "../services/api/endpoints";
import { LEGAL_LAST_UPDATED, TERMS_VERSION } from "../content/legal";
import { recordReconsent } from "../services/api/onboarding";
import { usePageMeta } from "../lib/usePageMeta";
import styles from "./TermsUpdated.module.css";

/**
 * Re-acceptance for an account that has already been through /welcome.
 *
 * ═══ WHY THIS IS A SEPARATE PAGE FROM /welcome ═══
 *
 * Because /welcome is written for someone who has just arrived, and sending a
 * returning customer there was actively harmful rather than merely untidy. It is
 * headed "Finish setting up", it promises "this is the only time you'll be
 * asked", and its four fields all start empty and required — including a date of
 * birth the API deliberately never returns, so it has to be retyped from memory.
 * The server then does the age arithmetic on whatever was typed and answers
 * 403 under_18 on a mistyped year, writing nothing and locking a paying account
 * out of a product it has already bought.
 *
 * That path had existed for weeks without ever running, because TERMS_VERSION
 * had never moved. Bumping it on 2026-08-26 switched it on, which is how it was
 * found — while the only stale accounts were test ones.
 *
 * ═══ IT ASKS FOR EXACTLY ONE THING ═══
 *
 * The terms changed; accept them. No name, no country, and NO DATE OF BIRTH —
 * `dobVerified` is already true on the profile, set by the server from a date it
 * verified itself. Re-asking proves nothing that is not already recorded and
 * reintroduces the lockout. Ned's call, 2026-08-26.
 *
 * ═══ IT RENDERS THE SAME ConsentNotice ═══
 *
 * Not decoration. The marketing consent is bundled into the acceptance, and
 * `recordReconsent` asserts `marketingOptIn: true` — so the notice that says so
 * has to be on screen when the button is pressed, or the record would describe
 * something the customer was never shown. Same component, same wording, same
 * source as /signup and /welcome, for the reason ConsentNotice itself gives.
 */
export function TermsUpdated() {
  usePageMeta("termsUpdated");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { session } = useAuth();
  const me = useMe();

  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setFailed(null);

    try {
      /* Straight back out as it came in. See recordReconsent on why these are
         resent rather than left to the handler's fallback. */
      await recordReconsent({
        termsVersion: TERMS_VERSION,
        givenName: me.data?.profile.givenName,
        familyName: me.data?.profile.familyName,
        country: me.data?.profile.country,
      });
    } catch (err) {
      /* The real reason, not a house message — and this one earns its place:
         the single unverified assumption behind this page is that the deployed
         handler accepts a body with no birthdate. If it does not, it answers
         400 and the message says so here rather than the page looking broken
         for a reason nobody can see. */
      setFailed(err instanceof Error ? err.message : "Something went wrong.");
      setBusy(false);
      return;
    }

    /*
     * ═══ READ IT BACK BEFORE BELIEVING IT ═══
     *
     * A 200 from the consent endpoint means the request was ACCEPTED. It does
     * not mean the stored version moved, and on 2026-08-26 those turned out to
     * be different things: the deployed handler answered 200 with a freshly
     * generated `recordedAt` while /me went on reporting the superseded version
     * and a consent row three days older.
     *
     * Without this check that is an infinite loop, and the worst-shaped one
     * available: accept, 200, navigate to /app, guard sees the old version,
     * divert back here, forever, with nothing on screen ever admitting a
     * failure. The customer is locked out of a product they have paid for by a
     * call that reported success. AgeGate shipped exactly this once — a write
     * that went to a store nothing read.
     *
     * So the guard's own source of truth is re-read and checked against the
     * value just sent. If it did not move, this is a failure and says so.
     */
    await qc.invalidateQueries({ queryKey: meKey(session?.userId) });

    /* fetchQuery, not getQueryData after the invalidate. `invalidateQueries`
       only refetches queries that are currently ACTIVE, so reading the cache
       straight afterwards can return the pre-acceptance answer for reasons that
       have nothing to do with the server — and this check reporting a failure
       that did not happen would be its own bug. This forces the read and awaits
       it; both calls target one key, so it is one request. */
    let fresh: { consent?: { termsVersion?: string | null } } | undefined;
    try {
      fresh = await qc.fetchQuery({ queryKey: meKey(session?.userId), queryFn: api.me });
    } catch {
      /* Could not re-read. NOT treated as a failed acceptance: the write may
         well have landed, and stranding someone over a flaky GET would be the
         same lockout by another route. Let them through — the guard re-reads on
         the next navigation anyway, and if the version really did not move they
         arrive back here rather than somewhere confusing. */
    }

    if (fresh && fresh.consent?.termsVersion !== TERMS_VERSION) {
      /* Deliberately not a retry prompt: pressing the button again would send
         the identical request to the same handler and get the identical answer.
         Nothing the customer can do from here fixes it, so the copy points at
         the only thing that can. */
      setFailed(
        "Your acceptance was sent but not saved. This is a fault on our side, not " +
          "yours, please contact support and we'll sort it out.",
      );
      setBusy(false);
      return;
    }

    /* No refreshSession, unlike /welcome. That call exists there to pick up a
       newly-written custom:dobVerified claim; nothing about this page changes a
       token claim, so there is nothing to refresh and no signed-out edge case to
       handle. */
    navigate("/app", { replace: true });
  }

  return (
    <Section eyebrow="Before you carry on" heading="Our terms have changed" tone="ink" level={1}>
      <form className={styles.form} onSubmit={onSubmit}>
        <p className={styles.body}>
          We&rsquo;ve updated the Terms of Service and Privacy Policy, last
          revised {LEGAL_LAST_UPDATED}. Nothing about your account, your plan or
          your credits has changed — we just need you to accept the current
          version before carrying on.
        </p>

        <p className={styles.body}>
          Read them in full: <Link to="/legal/terms">Terms &amp; Conditions</Link>{" "}
          and <Link to="/legal/privacy">Privacy Policy</Link>.
        </p>

        {failed && (
          <p className={styles.error} role="alert">
            We couldn&rsquo;t record that just now.{" "}
            <span className={styles.errorDetail}>{failed}</span>
          </p>
        )}

        <Cta type="submit" disabled={busy}>
          {busy ? "One sec…" : "Accept and continue"}
        </Cta>

        <ConsentNotice />
      </form>
    </Section>
  );
}
